import { useState, useEffect, useRef } from 'react'
import { Info } from 'react-feather';
import styled from 'styled-components';
import currency from 'helpers/currency';
import { Column } from 'styled/alignment/Column';
import colors from 'theme/colors';
import breakpoints from 'theme/breakpoints';
import DashCollapse from 'components/_UI/DashCollapse';
import InfoTooltip from 'components/_UI/InfoTooltip';
import Paginate from 'components/_UI/Paginate';
import Table from 'components/_UI/Table';
import Text from 'components/_UI/Text';
import Ilustration from 'components/_UI/Ilustration';
import Icon from 'components/_UI/Icon';
import Switch from 'components/_UI/Switch';
import EstablishmentOrderDetail from './EstablishmentOrderDetail';
import EstablishmentOrderStatusChips from './EstablishmentOrderStatusChips';
import EstablishmentOrderActionButtons from './EstablishmentOrderActionButtons';
import ordersApi from 'api/requests/orders';
import establishmentsApi from 'api/requests/establishments';
import { useUser } from 'context/user/User';
import { STATUS_KEY_TO_TYPE, } from 'modules/_common/EstablishmentOrderStatusTypes';

export default function EstablishmentOrdersTable({
    status,
    activeEvent,
    filters = {},
    toast,
}) {
    const { user } = useUser();
    const establishmentSlug = user?.establishments[0]?.slug;

    const [content, setContent] = useState([]);
    const [perPage, setPerpage] = useState(null);
    const [lastPage, setLastPage] = useState(null);
    const [currentPage, setCurrentPage] = useState(null);
    const [totalItems, setTotalItems] = useState(null);
    const [detail, setDetail] = useState();
    const [soundEnabled, setSoundEnabled] = useState(true);

    const soundEnabledRef = useRef(soundEnabled);
    const notificationAudioRef = useRef(null);

    const handleAdvanceStatus = async (order) => {
        try {
            const { data } = await ordersApi.advanceOrderFulfillmentStatus(order.id);

            setContent((previous) => {
                return previous.map((item) => {
                    if (item.id === order.id) {
                        return {
                            ...item,
                            status: data.data.status
                        }
                    }

                    return item;
                })
            })

            return data.data;
        } catch (error) {
            console.error('Failed to update order status:', error);
            alert('Failed to update order status');
        }
    };

    const handleAdvanceStatusFromCollapse = async (order) => {
        const updatedOrder = await handleAdvanceStatus(order);
        setDetail(false);
        toast.success(`Pedido ${updatedOrder?.order_id}: ${STATUS_KEY_TO_TYPE[updatedOrder?.status].label}`);
    }

    const handleReadyForPickupUpdate = async (order) => {
        try {
            const { data } = await ordersApi.updateOrderFulfillmentStatus(order.id, 'ready_for_pickup');

            setContent((previous) => {
                return previous.map((item) => {
                    if (item.id === order.id) {
                        return {
                            ...item,
                            status: data.data.status
                        }
                    }

                    return item;
                })
            })

            return data.data;
        } catch (error) {
            console.error('Failed to update order status:', error);
            alert('Failed to update order status');
        }
    }

    const handleReadyForPickupFromCollapse = async (order) => {
        const updatedOrder = await handleReadyForPickupUpdate(order);
        setDetail(false);
        toast.success(`Pedido ${updatedOrder?.order_id}: ${STATUS_KEY_TO_TYPE[updatedOrder?.status].label}`);
    }

    const hasDataChanged = (oldOrders, newOrders) => {
        if (oldOrders.length !== newOrders.length) return true;
        
        return oldOrders.some((oldOrder, index) => {
            const newOrder = newOrders[index];
            return oldOrder.id !== newOrder.id || 
                oldOrder.status !== newOrder.status || 
                oldOrder.total !== newOrder.total;
        });
    };

    const playNotificationSound = () => {
        if (notificationAudioRef.current && soundEnabledRef.current) {
            // Reset to start in case it's already playing
            notificationAudioRef.current.currentTime = 0;
            
            notificationAudioRef.current.play().catch(error => {
                console.error('Failed to play notification sound:', error);
            });
        }
    };

    const getOrders = async (page = 1) => {
        if (!activeEvent || !activeEvent.slug) {
            return
        }

        try {
            const { data } = await establishmentsApi.getEstablishmentOrders(
                establishmentSlug,
                activeEvent?.slug,
                {
                    ...filters,
                    status,
                    page,
                }
            )

            setContent(prevContent => {
                if (hasDataChanged(prevContent, data?.data)) {
                    setPerpage(data?.meta.per_page);
                    setLastPage(data.meta.last_page);
                    setCurrentPage(data?.meta.current_page);
                    setTotalItems(data?.meta.total);

                    playNotificationSound();
                    
                    return data?.data;
                }
                
                return prevContent;
            });
        } catch (error) {
            console.error(error);
        }
    }

    useEffect(() => {
        if (status !== 'all') {
            getOrders(currentPage || 1);
            return;
        }

        if (!activeEvent || !activeEvent.slug) return;

        let isMounted = true;
        let timeoutId;

        const pollOrders = async () => {
            if (!isMounted) return;

            await getOrders(currentPage || 1);

            timeoutId = setTimeout(pollOrders, 10000);
        };

        pollOrders();

        return () => {
            isMounted = false;
            if (timeoutId) clearTimeout(timeoutId);
        };
    }, [activeEvent, status, currentPage, filters]);

    useEffect(() => {
        soundEnabledRef.current = soundEnabled;
    }, [soundEnabled]);

    useEffect(() => {
        notificationAudioRef.current = new Audio('/assets/sounds/new_order_notification.mp3');
        
        notificationAudioRef.current.load();
        
        return () => {
            if (notificationAudioRef.current) {
                notificationAudioRef.current.pause();
                notificationAudioRef.current = null;
            }
        };
    }, []);

    const ordersList = content?.map((order) => {
        const date = new Date(order?.created_at);

        return {
            order_id: <Text name='small'>{order?.id}</Text>,
            name: <Text name='small'>{order?.customer.name}</Text>,
            date: <Text name='small'>
                {date.toLocaleDateString('pt-BR')} - {date.toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
            </Text>,
            value: <Text name='small'>{currency.format(order?.total)}</Text>,
            status: <EstablishmentOrderStatusChips status={order?.status} />,
            actions:
                <>
                    <ContentAction
                    >
                        <EstablishmentOrderActionButtons
                            order={order}
                            onClick={handleAdvanceStatus}
                        />
                    </ContentAction>
                    <ContentAction
                        onClick={() => handleReadyForPickupUpdate(order)}
                    >
                        <InfoTooltip
                            bgColor={colors.night}
                            text={'Pedido pronto para retirada'}
                            type='none'
                        >
                            <StyledIcon>
                                <Icon color={colors.successDark} name="check-point" />
                            </StyledIcon>
                        </InfoTooltip>
                    </ContentAction>
                    <ContentAction
                        onClick={() => setDetail(order)}
                    >
                        <InfoTooltip
                            bgColor={colors.night}
                            text={'Ver detalhes'}
                            type='none'
                        >
                            <Info size={20} color={colors.primaryLight} />
                        </InfoTooltip>
                    </ContentAction>
                </>
        };
    })

    return (
        <Container>
            {
                content?.length <= 0 ? (
                    <Column fullyCentralized>
                        <Column width='auto' height='300px'>
                            <Ilustration name='orders' />
                        </Column>
                        <Text marginTop={'15px'} name='highlight'>Você ainda não possui pedidos.</Text>
                    </Column>
                ) : (
                    <>
                        <TableOptions>
                            <Switch
                                label='Habilitar notificação sonora'
                                labelSize='small'
                                checked={soundEnabled}
                                onChange={setSoundEnabled}
                            />
                        </TableOptions>
                        <Table
                            rows={ordersList}
                            columns={['ID do pedido', 'Cliente', 'Data', 'Valor', 'Status', 'Ações']}
                        />

                        {
                            lastPage > 1 && (
                                <Paginate
                                    perPage={perPage}
                                    totalItems={totalItems}
                                    lastPage={lastPage}
                                    firstPage={1}
                                    currentPage={currentPage}
                                    marginTop={'30px'}
                                    onPaginate={(number) => getOrders(number)}
                                />
                            )
                        }
                    </>
                )
            }

            <DashCollapse visible={detail} onClose={() => setDetail(false)}>
                {
                    detail &&
                        <EstablishmentOrderDetail
                            order={detail}
                            handleStatusUpdate={handleAdvanceStatusFromCollapse}
                            handleReadyForPickupUpdate={handleReadyForPickupFromCollapse}
                        />
                }
            </DashCollapse>
        </Container >

    )
}

const Container = styled(Column)`
    min-height: 300px;
    margin-top: 30px;
    border-radius: 8px;
    background-color: ${colors.neutralLight};
    padding: 20px;
    box-shadow: 1px 1px 4px #ddd;
    justify-content: center;
`;

const ContentAction = styled.div`
    cursor: pointer;
    display: flex;
    align-items: center;
    transition: all ease-in-out 0.25s;
    margin: ${props => props.margin || ''};

    & > div > svg {
        &:hover {
            transform: scale(1.1);
       }
    }
`

const StyledIcon = styled.div`
    margin-right: 8px;
    cursor: pointer;
    width: 20px;
    height: 20px;
`;

const TableOptions = styled.div`
    margin-right: auto;
    margin-left: unset;

    @media screen and (min-width: ${breakpoints.sm}) {
        margin-left: auto;
        margin-right: unset;
    }
`